/**
 * Integrytas Group - Common Security Module (CSM)
 * Set-new-password screen.
 *
 * Rendered when Supabase emits an onAuthStateChange event of type
 * PASSWORD_RECOVERY (the user clicked the reset link in the email and the
 * SDK has consumed the access_token from the URL fragment). The user is
 * technically signed in at this point - but only with the recovery
 * privilege. They must set a new password before continuing.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';
import { Watermark } from '../common/Watermark';

export interface SetNewPasswordScreenProps {
  onSubmit:    (newPassword: string) => Promise<void>;
  onCancel:    () => Promise<void>;          // sign out + return to login
  errorMessage?: string | null;
}

export function SetNewPasswordScreen({
  onSubmit, onCancel, errorMessage,
}: SetNewPasswordScreenProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < 8) {
      setLocalError(t('auth.set_new_password.password_min_error'));
      return;
    }
    if (password !== confirm) {
      setLocalError(t('auth.passwords_dont_match'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit(password);
      setDone(true);
    } catch (e) {
      setLocalError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
      <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />

      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded border border-csm-border bg-white p-6 shadow-sm"
      >
        <h1 className="mb-1 text-csm-lg font-semibold text-csm-ink">
          {t('auth.set_new_password.title')}
        </h1>
        <p className="mb-4 text-csm-sm text-csm-muted">
          {t('auth.set_new_password.subtitle', { app: APPLICATION_NAME })}
        </p>

        {done ? (
          <>
            <div className="mb-3 rounded border border-csm-success-border bg-csm-success-soft px-2 py-2 text-csm-sm text-csm-success">
              {t('auth.set_new_password.done_message')}
            </div>
          </>
        ) : (
          <>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
              {t('auth.set_new_password.new_password_label')}
            </label>
            <input
              type="password"
              required minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
            />

            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
              {t('auth.set_new_password.confirm_new_password_label')}
            </label>
            <input
              type="password"
              required minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
            />

            {(localError || errorMessage) && (
              <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
                {localError ?? errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="mb-3 h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {busy ? t('auth.set_new_password.submitting') : t('auth.set_new_password.submit')}
            </button>

            <button
              type="button"
              onClick={() => void onCancel()}
              className="h-8 w-full rounded border border-csm-border bg-white text-csm-sm text-csm-ink-soft hover:bg-csm-surface-alt"
            >
              {t('auth.set_new_password.cancel_and_sign_out')}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
